import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ApiService } from '@core/api.service';
import { DeviationActionsService } from '@features/project/deviations/deviations-list/deviation-actions.service';
import { CrudService } from 'collection';
import { AuthService } from '@features/auth/auth.service';
import { Item, ItemModel } from 'collection';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'create-deviation-modal',
  templateUrl: './create-deviation-modal.component.html',
  styleUrls: ['./create-deviation-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateDeviationModalComponent implements OnInit {
  deviationControls$ = new Subject();
  deviationItem: ItemModel;
  projectItem: ItemModel;

  isCreateMode: boolean = true;
  isUserCanEdit: boolean;
  isUserCanDelete: boolean;

  private _destroyed$ = new Subject();

  close(): void {
    this._dialogRef.close();
  }

  deviationCreated(item): void {
    this.data.subject.next({
      confirmed: true,
      modal: this._dialogRef,
      item: item
    });
    this.close();
  }

  loadProject() {
    const projectId = this.data.projectId;
    return this.projectItem.setParams('id', projectId).load();
  }

  loadDeviation(): void {
    const deviationId = this.data.item.id;
    this.deviationItem.setParams('id', deviationId).load().subscribe();
  }

  private initDeviation(): void {
    if (this.projectItem.loaded) {
      this.loadDeviation();
    } else {
      this.projectItem.onLoad$.pipe(takeUntil(this._destroyed$)).subscribe(() => {
        this.loadDeviation();
        this.isUserCanEdit = this.isEditAvailable(this.deviationItem.data, this.isCreateMode);
        this.isUserCanDelete = this.isDeleteAvailable(this.deviationItem.data);
      });
    }
  }

  submit(): void {
    this.deviationControls$.next('submit');
  }

  private isEditAvailable(item, isCreateMode: boolean): boolean {
    return this._deviationActionsService.isEditAvailable(item, isCreateMode);
  }

  private isDeleteAvailable(item): boolean {
    return this._deviationActionsService.isDeleteAvailable(item);
  }

  ngOnInit(): void {
    this.loadProject().subscribe(() => {
      if (this.data?.item?.id) {
        this.initDeviation();
      }
    });

    this.isUserCanEdit = this.isEditAvailable(this.deviationItem.data, this.isCreateMode);
    this.isUserCanDelete = this.isDeleteAvailable(this.deviationItem.data);
  }

  constructor(
    public _dialogRef: MatDialogRef<any>,
    private _crud: CrudService,
    private _deviationActionsService: DeviationActionsService,
    private _auth: AuthService,
    private _api: ApiService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    if (this.data?.item) {
      this.isCreateMode = false;
    }

    this.deviationItem = new Item({
      api: this._crud.createEntity({ name: 'deviations' }),
      params: {
        expand: 'project,client,tags,user,assigned_user,type,incident,images_count'
      }
    });

    this.projectItem = new Item({
      api: this._api.projects,
      params: {
        expand: 'client,user,total_time_minutes,subprojects.client,subprojects.display_status,project_location'
      }
    });
  }
}
